/**
 * Face-edge kernel — topology structure layer (K0).
 *
 * Everything that maintains the graph WITHOUT deciding geometry policy: vertex
 * welding, edge add/dedupe/split (loop-splice preserving), face bookkeeping,
 * orphan GC, invariants, isSolid, connected components, and topology merge.
 * The arrangement policy (what an inserted segment DOES) lives in arrange.ts.
 */

import { nanoid } from 'nanoid'
import type { BodyTopology, BodyPt3, BodyVid, BodyEid, BodyFid, BodyFace, BodyLoop } from './types'
import {
  WELD_TOL, AREA_EPS, distSq, dist, planeDist, frameForPlane, to2,
  pointSegDist, segSegClosest,
} from './geom3'
import type { Pt2 } from './geom3'

const mint = () => nanoid(12)
export const newVid = (): BodyVid => 'v' + mint()
export const newEid = (): BodyEid => 'e' + mint()
export const newFid = (): BodyFid => 'f' + mint()

export function createTopology(): BodyTopology {
  return { vertices: {}, edges: {}, faces: {}, curves: {} }
}

/** find an existing vertex within WELD_TOL of p (linear scan — K0 correctness-first;
 *  a spatial index is a recorded later optimization, not a semantics change) */
export function findVertexNear(topo: BodyTopology, p: BodyPt3, tol = WELD_TOL): BodyVid | null {
  const tolSq = tol * tol
  let best: BodyVid | null = null
  let bestD = tolSq
  for (const [vid, q] of Object.entries(topo.vertices)) {
    const d = distSq(p, q)
    if (d <= bestD) { best = vid; bestD = d }
  }
  return best
}

/** weld p onto an existing vertex or create one — THE way points enter the graph */
export function weldVertex(topo: BodyTopology, p: BodyPt3): BodyVid {
  const near = findVertexNear(topo, p)
  if (near) return near
  const vid = newVid()
  topo.vertices[vid] = { x: p.x, y: p.y, z: p.z }
  return vid
}

const edgePairKey = (a: BodyVid, b: BodyVid) => (a < b ? a + '|' + b : b + '|' + a)

export function findEdgeBetween(topo: BodyTopology, a: BodyVid, b: BodyVid): BodyEid | null {
  for (const [eid, e] of Object.entries(topo.edges)) {
    if ((e.a === a && e.b === b) || (e.a === b && e.b === a)) return eid
  }
  return null
}

/** add an edge between two existing vertices; dedupes (returns the existing eid).
 *  Returns null for a degenerate (a === b) request. */
export function addEdge(topo: BodyTopology, a: BodyVid, b: BodyVid): { eid: BodyEid; existed: boolean } | null {
  if (a === b) return null
  const existing = findEdgeBetween(topo, a, b)
  if (existing) return { eid: existing, existed: true }
  const eid = newEid()
  topo.edges[eid] = { a, b }
  return { eid, existed: false }
}

/** every face loop referencing eid, with positions */
export function facesUsingEdge(topo: BodyTopology, eid: BodyEid): BodyFid[] {
  const out: BodyFid[] = []
  for (const [fid, f] of Object.entries(topo.faces)) {
    if (f.loops.some((l) => l.edges.includes(eid))) out.push(fid)
  }
  return out
}

/**
 * Split an edge at an existing vertex m (assumed on the segment): eid is replaced
 * by two edges, and every face loop referencing it gets the pair spliced in with
 * traversal order preserved. Returns the two new eids [aSide, bSide].
 */
export function splitEdge(topo: BodyTopology, eid: BodyEid, m: BodyVid): [BodyEid, BodyEid] {
  const e = topo.edges[eid]
  if (!e) throw new Error('splitEdge: unknown edge ' + eid)
  if (m === e.a || m === e.b) throw new Error('splitEdge: split vertex is an endpoint')
  const e1 = newEid() // a → m
  const e2 = newEid() // m → b
  topo.edges[e1] = { a: e.a, b: m, curve: e.curve, soft: e.soft }
  topo.edges[e2] = { a: m, b: e.b, curve: e.curve, soft: e.soft }
  delete topo.edges[eid]
  for (const f of Object.values(topo.faces)) {
    for (const loop of f.loops) {
      for (let i = loop.edges.length - 1; i >= 0; i--) {
        if (loop.edges[i] !== eid) continue
        const dir = loop.dirs[i]
        // traversed a→b: a→m then m→b; traversed b→a: b→m (e2 reversed) then m→a (e1 reversed)
        const repl = dir ? [e1, e2] : [e2, e1]
        const dirs = dir ? [true, true] : [false, false]
        loop.edges.splice(i, 1, ...repl)
        loop.dirs.splice(i, 1, ...dirs)
      }
    }
  }
  return [e1, e2]
}

/** remove vertices no edge references (call after any removal pass) */
export function gcOrphanVertices(topo: BodyTopology): void {
  const used = new Set<BodyVid>()
  for (const e of Object.values(topo.edges)) { used.add(e.a); used.add(e.b) }
  for (const vid of Object.keys(topo.vertices)) {
    if (!used.has(vid)) delete topo.vertices[vid]
  }
}

/** walk a loop into its ordered vertex ring; throws on a broken chain */
export function loopVertices(topo: BodyTopology, loop: BodyLoop): BodyVid[] {
  const out: BodyVid[] = []
  for (let i = 0; i < loop.edges.length; i++) {
    const e = topo.edges[loop.edges[i]]
    if (!e) throw new Error('loopVertices: loop references missing edge')
    out.push(loop.dirs[i] ? e.a : e.b)
  }
  return out
}

export function loopPoints(topo: BodyTopology, loop: BodyLoop): BodyPt3[] {
  return loopVertices(topo, loop).map((vid) => topo.vertices[vid])
}

/** shoelace area of a loop projected into a plane frame (sign = orientation about +n) */
export function loopArea2(topo: BodyTopology, loop: BodyLoop, frame: ReturnType<typeof frameForPlane>): number {
  const pts = loopPoints(topo, loop).map((p) => to2(frame, p))
  return polygonArea(pts)
}

export function polygonArea(pts: Pt2[]): number {
  let s = 0
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]
    const q = pts[(i + 1) % pts.length]
    s += p.x * q.y - q.x * p.y
  }
  return s / 2
}

/** even-odd point-in-polygon (single ring) */
export function pointInRing(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]
    const b = ring[j]
    if (a.y > pt.y !== b.y > pt.y) {
      const x = a.x + ((pt.y - a.y) / (b.y - a.y)) * (b.x - a.x)
      if (pt.x < x) inside = !inside
    }
  }
  return inside
}

/** even-odd across outer + holes: inside the REGION (in outer, outside holes) */
export function pointInRegion(pt: Pt2, outer: Pt2[], holes: Pt2[][]): boolean {
  if (!pointInRing(pt, outer)) return false
  for (const h of holes) if (pointInRing(pt, h)) return false
  return true
}

/** min distance from a 2D point to any segment of the given rings */
export function distToRings(pt: Pt2, rings: Pt2[][]): number {
  let best = Infinity
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i]
      const b = ring[(i + 1) % ring.length]
      const abx = b.x - a.x, aby = b.y - a.y
      const l2 = abx * abx + aby * aby
      let t = l2 > 0 ? ((pt.x - a.x) * abx + (pt.y - a.y) * aby) / l2 : 0
      t = Math.max(0, Math.min(1, t))
      const dx = pt.x - (a.x + abx * t), dy = pt.y - (a.y + aby * t)
      best = Math.min(best, Math.sqrt(dx * dx + dy * dy))
    }
  }
  return best
}

/** a face's rings projected into its own plane frame: [outer, ...holes] */
export function faceRings2(topo: BodyTopology, face: BodyFace): { frame: ReturnType<typeof frameForPlane>; rings: Pt2[][] } {
  const frame = frameForPlane(face.plane)
  const rings = face.loops.map((l) => loopPoints(topo, l).map((p) => to2(frame, p)))
  return { frame, rings }
}

/** a robust interior point of a region (scanline midpoint — handles holes) */
export function regionInteriorPoint(outer: Pt2[], holes: Pt2[][]): Pt2 | null {
  const ys = outer.map((p) => p.y).sort((a, b) => a - b)
  const candidates: number[] = []
  for (let i = 0; i + 1 < ys.length; i++) {
    if (ys[i + 1] - ys[i] > 1e-12) candidates.push((ys[i] + ys[i + 1]) / 2)
  }
  candidates.push((ys[0] + ys[ys.length - 1]) / 2)
  const allRings = [outer, ...holes]
  for (const y of candidates) {
    const xs: number[] = []
    for (const ring of allRings) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i]
        const b = ring[j]
        if (a.y > y !== b.y > y) xs.push(a.x + ((y - a.y) / (b.y - a.y)) * (b.x - a.x))
      }
    }
    xs.sort((p, q) => p - q)
    if (xs.length >= 2 && xs[1] - xs[0] > 1e-9) {
      const pt = { x: (xs[0] + xs[1]) / 2, y }
      if (pointInRegion(pt, outer, holes)) return pt
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// isSolid — watertight closed volume: every edge bounded by exactly two face
// traversals in OPPOSITE directions (consistent winding), and nothing dangling.
// Purely informational + the poché gate (spike §5.2).
// ---------------------------------------------------------------------------

export function isSolid(topo: BodyTopology): boolean {
  const fids = Object.keys(topo.faces)
  if (fids.length < 4) return false
  // 1) two-manifold: every edge used by exactly two loop traversals
  const uses = new Map<BodyEid, { fid: BodyFid; dir: boolean }[]>()
  for (const [fid, f] of Object.entries(topo.faces)) {
    for (const loop of f.loops) {
      for (let i = 0; i < loop.edges.length; i++) {
        const list = uses.get(loop.edges[i]) || []
        list.push({ fid, dir: loop.dirs[i] })
        uses.set(loop.edges[i], list)
      }
    }
  }
  for (const eid of Object.keys(topo.edges)) {
    if ((uses.get(eid) || []).length !== 2) return false
  }
  // 2) orientable: a consistent flip assignment exists so every shared edge is
  // traversed in opposite effective directions (stored normals may point
  // arbitrarily — healing assigns them per canonical plane, not per volume)
  const flip = new Map<BodyFid, boolean>()
  for (const start of fids) {
    if (flip.has(start)) continue
    flip.set(start, false)
    const queue = [start]
    while (queue.length) {
      const fid = queue.pop()!
      const f = topo.faces[fid]
      const myFlip = flip.get(fid)!
      for (const loop of f.loops) {
        for (let i = 0; i < loop.edges.length; i++) {
          const pair = uses.get(loop.edges[i])!
          const other = pair[0].fid === fid && pair[0].dir === loop.dirs[i] ? pair[1] : pair[0]
          if (other.fid === fid) continue // same face twice on one edge — fold onto itself
          const effMine = loop.dirs[i] !== myFlip
          const wantOtherEff = !effMine
          const otherFlip = other.dir !== wantOtherEff
          if (!flip.has(other.fid)) { flip.set(other.fid, otherFlip); queue.push(other.fid) }
          else if (flip.get(other.fid) !== otherFlip) return false
        }
      }
    }
  }
  return true
}

// ---------------------------------------------------------------------------
// Connected components + merge (element identity, spike §3.3 — splitWall's model)
// ---------------------------------------------------------------------------

/** vertex-connectivity components; returns the topology partitioned (largest first) */
export function componentsOf(topo: BodyTopology): BodyTopology[] {
  const parent = new Map<BodyVid, BodyVid>()
  const find = (x: BodyVid): BodyVid => {
    let r = x
    while (parent.get(r) !== r) r = parent.get(r)!
    let c = x
    while (parent.get(c) !== c) { const n = parent.get(c)!; parent.set(c, r); c = n }
    return r
  }
  for (const vid of Object.keys(topo.vertices)) parent.set(vid, vid)
  for (const e of Object.values(topo.edges)) {
    const ra = find(e.a)
    const rb = find(e.b)
    if (ra !== rb) parent.set(ra, rb)
  }
  const byRoot = new Map<BodyVid, BodyTopology>()
  const compOf = (vid: BodyVid): BodyTopology => {
    const root = find(vid)
    let t = byRoot.get(root)
    if (!t) { t = createTopology(); byRoot.set(root, t) }
    return t
  }
  for (const [vid, p] of Object.entries(topo.vertices)) compOf(vid).vertices[vid] = p
  for (const [eid, e] of Object.entries(topo.edges)) compOf(e.a).edges[eid] = e
  for (const [fid, f] of Object.entries(topo.faces)) {
    const anchor = topo.edges[f.loops[0]?.edges[0] ?? '']
    if (anchor) compOf(anchor.a).faces[fid] = f
  }
  // curves ride with any component holding one of their edges
  for (const [cid, c] of Object.entries(topo.curves)) {
    for (const t of byRoot.values()) {
      if (Object.values(t.edges).some((e) => e.curve === cid)) { t.curves[cid] = c; break }
    }
  }
  const out = [...byRoot.values()]
  const size = (t: BodyTopology) => Object.keys(t.edges).length + Object.keys(t.faces).length
  out.sort((a, b) => size(b) - size(a))
  return out
}

/**
 * Merge two id-disjoint topologies, welding coincident vertices and deduping
 * coincident edges (the cross-body stickiness primitive — orchestration of which
 * ELEMENTS merge is the commands layer's job, K3).
 */
export function mergeTopologies(a: BodyTopology, b: BodyTopology): BodyTopology {
  const out: BodyTopology = {
    vertices: { ...a.vertices },
    edges: { ...a.edges },
    faces: { ...a.faces },
    curves: { ...a.curves, ...b.curves },
  }
  // weld b's vertices onto a's
  const vmap = new Map<BodyVid, BodyVid>()
  for (const [vid, p] of Object.entries(b.vertices)) {
    const near = findVertexNear(out, p)
    if (near) vmap.set(vid, near)
    else { out.vertices[vid] = p; vmap.set(vid, vid) }
  }
  // remap + dedupe b's edges
  const emap = new Map<BodyEid, { eid: BodyEid; flipped: boolean }>()
  for (const [eid, e] of Object.entries(b.edges)) {
    const na = vmap.get(e.a)!
    const nb = vmap.get(e.b)!
    if (na === nb) continue // collapsed by welding — degenerate, drop
    const existing = findEdgeBetween(out, na, nb)
    if (existing) {
      emap.set(eid, { eid: existing, flipped: out.edges[existing].a !== na })
    } else {
      out.edges[eid] = { ...e, a: na, b: nb }
      emap.set(eid, { eid, flipped: false })
    }
  }
  // remap b's face loops
  for (const [fid, f] of Object.entries(b.faces)) {
    const loops: BodyLoop[] = []
    let ok = true
    for (const loop of f.loops) {
      const edges: BodyEid[] = []
      const dirs: boolean[] = []
      for (let i = 0; i < loop.edges.length; i++) {
        const m = emap.get(loop.edges[i])
        if (!m) { ok = false; break }
        edges.push(m.eid)
        dirs.push(m.flipped ? !loop.dirs[i] : loop.dirs[i])
      }
      if (!ok) break
      loops.push({ edges, dirs })
    }
    if (ok) out.faces[fid] = { ...f, loops }
  }
  return out
}

// ---------------------------------------------------------------------------
// Invariants — the test harness's teeth. Returns human-readable violations;
// empty array = healthy. O(n²) checks are deliberate (tests, not hot paths).
// ---------------------------------------------------------------------------

export function invariants(topo: BodyTopology): string[] {
  const bad: string[] = []
  const vids = Object.keys(topo.vertices)
  const eids = Object.keys(topo.edges)

  // vertices: no two within tolerance
  for (let i = 0; i < vids.length; i++) {
    for (let j = i + 1; j < vids.length; j++) {
      if (distSq(topo.vertices[vids[i]], topo.vertices[vids[j]]) < WELD_TOL_SQ_STRICT) {
        bad.push(`vertices ${vids[i]}/${vids[j]} within weld tolerance`)
      }
    }
  }

  // edges: endpoints exist, non-degenerate, no duplicates, every vertex used
  const seenPairs = new Set<string>()
  const usedV = new Set<BodyVid>()
  for (const eid of eids) {
    const e = topo.edges[eid]
    if (!topo.vertices[e.a] || !topo.vertices[e.b]) { bad.push(`edge ${eid} references missing vertex`); continue }
    if (e.a === e.b) bad.push(`edge ${eid} is a self-loop`)
    if (dist(topo.vertices[e.a], topo.vertices[e.b]) < WELD_TOL / 2) bad.push(`edge ${eid} shorter than tolerance`)
    const key = edgePairKey(e.a, e.b)
    if (seenPairs.has(key)) bad.push(`duplicate edge between ${e.a}/${e.b}`)
    seenPairs.add(key)
    usedV.add(e.a)
    usedV.add(e.b)
  }
  for (const vid of vids) if (!usedV.has(vid)) bad.push(`orphan vertex ${vid}`)

  // the arrangement property: no two edges cross at interior points
  for (let i = 0; i < eids.length; i++) {
    for (let j = i + 1; j < eids.length; j++) {
      const e1 = topo.edges[eids[i]]
      const e2 = topo.edges[eids[j]]
      if (e1.a === e2.a || e1.a === e2.b || e1.b === e2.a || e1.b === e2.b) continue
      const r = segSegClosest(
        topo.vertices[e1.a], topo.vertices[e1.b],
        topo.vertices[e2.a], topo.vertices[e2.b],
      )
      if (r.d < WELD_TOL / 2) bad.push(`edges ${eids[i]}/${eids[j]} cross away from shared vertices`)
    }
  }

  // faces: loops closed/connected, ≥3 edges, planar, oriented (outer CCW, holes CW)
  for (const [fid, f] of Object.entries(topo.faces)) {
    if (!f.loops.length) { bad.push(`face ${fid} has no loops`); continue }
    let broken = false
    for (const loop of f.loops) {
      if (loop.edges.length < 3) { bad.push(`face ${fid} loop shorter than 3 edges`); broken = true; continue }
      if (loop.edges.length !== loop.dirs.length) { bad.push(`face ${fid} loop edges/dirs mismatch`); broken = true; continue }
      for (let i = 0; i < loop.edges.length; i++) {
        const e = topo.edges[loop.edges[i]]
        const nx = topo.edges[loop.edges[(i + 1) % loop.edges.length]]
        if (!e || !nx) { bad.push(`face ${fid} loop references missing edge`); broken = true; break }
        const head = loop.dirs[i] ? e.b : e.a
        const nextTail = loop.dirs[(i + 1) % loop.edges.length] ? nx.a : nx.b
        if (head !== nextTail) { bad.push(`face ${fid} loop not connected at position ${i}`); broken = true; break }
      }
    }
    if (broken) continue
    const frame = frameForPlane(f.plane)
    for (const loop of f.loops) {
      for (const p of loopPoints(topo, loop)) {
        if (Math.abs(planeDist(f.plane, p)) > WELD_TOL * 2) {
          bad.push(`face ${fid} has a vertex off its plane`)
          break
        }
      }
    }
    const a0 = loopArea2(topo, f.loops[0], frame)
    if (a0 < AREA_EPS) bad.push(`face ${fid} outer loop not CCW (or degenerate)`)
    for (let i = 1; i < f.loops.length; i++) {
      if (loopArea2(topo, f.loops[i], frame) > -AREA_EPS) bad.push(`face ${fid} hole ${i} not CW (or degenerate)`)
    }
  }

  return bad
}

// strict = (tol * 0.99)² so that points welded AT tolerance don't false-positive
const WELD_TOL_SQ_STRICT = (WELD_TOL * 0.99) ** 2

export { pointSegDist }
